import type {
  Applicant,
  PayFrequency,
  PurchaseTimeframe,
  RoadmapAnswers,
  RoadmapResults,
} from '../types/roadmap'

/** Days between pays, used to project a completion date. */
export const PAY_CYCLE_DAYS: Record<PayFrequency, number> = {
  weekly: 7,
  fortnightly: 14,
  monthly: 30.44,
}

/** Outer edge of each timeframe in months, for the "behind schedule" check. */
const TIMEFRAME_MONTHS: Record<PurchaseTimeframe, number> = {
  '0-6m': 6,
  '6-12m': 12,
  '1-2y': 24,
  '2y+': 36,
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value))
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function applicantIncome(applicant: Applicant): number {
  return (
    applicant.grossAnnualIncome +
    applicant.extraIncome +
    applicant.rentalOtherIncome
  )
}

/**
 * Typical purchasing costs. Deliberately a simple percentage of the property
 * price — this is an estimate offered to people who have no figure at all, not
 * a stamp duty calculation, and it is always labelled as an estimate.
 */
export function estimatePurchasingCosts(propertyPrice: number): number {
  if (propertyPrice <= 0) return 0
  return Math.round((propertyPrice * 0.05) / 500) * 500
}

/** A typical post-settlement buffer: roughly three months of living costs. */
export function estimateCashBuffer(livingExpenses: number): number {
  const monthly = livingExpenses > 0 ? livingExpenses : 3500
  return Math.round((monthly * 3) / 500) * 500
}

export function addDays(from: Date, days: number): Date {
  const next = new Date(from)
  next.setDate(next.getDate() + Math.ceil(days))
  return next
}

export function calculateResults(
  answers: RoadmapAnswers,
  today: Date = new Date(),
): RoadmapResults {
  const isInvestor = answers.goal === 'first-investment'

  const totalSavingsGoal =
    answers.deposit +
    answers.purchasingCosts +
    answers.cashBuffer +
    (isInvestor ? answers.investorReserve : 0) +
    answers.otherCosts

  // Money kept untouched is deliberately excluded — it is not available to put
  // toward the purchase, so counting it would overstate their position.
  const usableSavings = Math.max(
    0,
    answers.savedSoFar +
      answers.giftAmount +
      answers.otherConfirmedFunds -
      answers.keepUntouched,
  )

  const rawGap = totalSavingsGoal - usableSavings
  const goalReached = totalSavingsGoal > 0 && rawGap <= 0
  const savingsGap = Math.max(0, rawGap)

  const progress =
    totalSavingsGoal > 0
      ? Math.min(1, usableSavings / totalSavingsGoal)
      : 0

  // No savings amount means no timeline. We show a prompt rather than an
  // infinite or nonsensical date.
  let paysNeeded: number | null = null
  let completionDate: Date | null = null
  if (goalReached) {
    paysNeeded = 0
    completionDate = today
  } else if (answers.couldSavePerPay > 0 && answers.payFrequency) {
    paysNeeded = Math.ceil(savingsGap / answers.couldSavePerPay)
    completionDate = addDays(
      today,
      paysNeeded * PAY_CYCLE_DAYS[answers.payFrequency],
    )
  }

  let behindTimeframe = false
  if (completionDate && answers.timeframe && !goalReached) {
    const deadline = new Date(today)
    deadline.setMonth(deadline.getMonth() + TIMEFRAME_MONTHS[answers.timeframe])
    behindTimeframe = completionDate > deadline
  }

  const totalHouseholdIncome =
    applicantIncome(answers.applicantOne) +
    (answers.buyingWith === 'together'
      ? applicantIncome(answers.applicantTwo)
      : 0)

  const totalMonthlyCommitments =
    answers.currentRent + answers.livingExpenses

  return {
    totalSavingsGoal,
    usableSavings,
    savingsGap,
    progress,
    goalReached,
    paysNeeded,
    completionDate,
    behindTimeframe,
    totalHouseholdIncome,
    totalMonthlyCommitments,
  }
}

export function reviewDate(today: Date = new Date()): Date {
  return addDays(today, 90)
}
